import { Lock } from '@assets/svg';
import { DatePicker, Dropdown, Flex, Input, Text } from '@components';
import { Box, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import countriesJSON from '@assets/countries.json';
import { getFlagEmoji } from '@utils/functions/getFlagFromCountryCode';
import { useCountriesQuery } from '@features/api/appDataSlice';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import {
  UpdatePersonalDetailsFormData,
  UpdatePersonalDetailsProps,
} from './UpdatePersonalDetails.types';
import { noop } from 'lodash';
import {
  StyledButton,
  StyledRowContainer,
} from './UpdatePersonalDetails.styles';
import { updatePersonalDetailsFormSchema } from '@validationSchemas/updateProfile';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useUpdateUserInfoMutation,
  useUserQuery,
} from '@features/api/userSlice';

const UpdatePersonalDetails: React.FC<UpdatePersonalDetailsProps> = ({
  onClose,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const { data: countries } = useCountriesQuery();
  const { data: user } = useUserQuery();
  const [updateUserInfoMutation] = useUpdateUserInfoMutation();
  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors, isValid, dirtyFields },
  } = useForm<UpdatePersonalDetailsFormData>({
    resolver: yupResolver(updatePersonalDetailsFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      firstName: user?.basicDetails.firstName ?? '',
      middleName: user?.basicDetails.middleName ?? '',
      lastName: user?.basicDetails.lastName ?? '',
      birthDate: user?.basicDetails.dateOfBirth
        ? new Date(user?.basicDetails.dateOfBirth)
        : undefined,
      email: user?.email ?? '',
      phoneCountryCode: user?.contactDetails?.phoneCountryCode ?? '',
      phoneNumber: user?.contactDetails?.phoneNumber ?? '',
      address: user?.contactDetails?.streetAddress ?? '',
      city: user?.contactDetails?.city ?? '',
      state: user?.contactDetails?.state ?? '',
      zipCode: user?.contactDetails?.zipCode ?? '',
      contactDetailsCountry: user?.contactDetails?.country ?? '',
    },
  });

  const number = watch('phoneNumber');
  const code = watch('phoneCountryCode');
  useEffect(() => {
    if (code !== undefined) {
      trigger('phoneNumber');
    }
    if (number !== undefined) {
      trigger('phoneCountryCode');
    }
  }, [number, code, trigger]);

  const [countryDialCodesWithFlag] = useMemo(() => {
    const countriesArray = countriesJSON as Array<any>;
    return [
      countriesArray.map((x) => ({
        name: `${x.name} (${x.dialling_code})`,
        value: x.dialling_code,
        icon: getFlagEmoji(x.code),
      })) ?? [],
    ];
  }, []);

  const countriesMenuItems = useMemo(() => {
    return (
      countries?.map((x) => ({
        name: x.name,
        value: x.name,
        icon: x.flag,
      })) ?? []
    );
  }, [countries]);

  const onSubmit = async (data: UpdatePersonalDetailsFormData) => {
    if (!user) return;

    try {
      await updateUserInfoMutation({
        ...user,
        contactDetails: {
          ...user.contactDetails,
          phoneCountryCode: data.phoneCountryCode ?? undefined,
          phoneNumber: data.phoneNumber ?? undefined,
          streetAddress: data.address ?? undefined,
          city: data.city ?? undefined,
          state: data.state ?? undefined,
          zipCode: data.zipCode ?? undefined,
          country: data.contactDetailsCountry ?? undefined,
        },
      }).unwrap();

      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex flexDirection='column' gap={12}>
        <Flex mt={14} flexDirection='column'>
          <Flex alignItems='center'>
            <Lock color={theme.palette.grey.A100} />
            <Text
              variant='h6'
              ml={5}
              fontWeight='fontWeightSemiBold'
              color={theme.palette.grey.A100}
            >
              <FormattedMessage id='investment.personalInformation.fullName' />
            </Text>
          </Flex>
          <Flex mt={6} gap={10}>
            <Input
              label={intl.formatMessage({
                id: 'signUp.form.firstName',
              })}
              fullWidth
              disabled
              inputProps={{
                'data-testid': 'first-name',
              }}
              {...registerInput(register, 'firstName')}
              {...getErrorProps(intl, errors.firstName)}
            />
            <Input
              label={intl.formatMessage({
                id: 'signUp.form.middleName',
              })}
              inputProps={{
                'data-testid': 'middle-name',
              }}
              fullWidth
              disabled
              {...registerInput(register, 'middleName')}
              {...getErrorProps(intl, errors.middleName)}
            />
            <Input
              label={intl.formatMessage({
                id: 'signUp.form.surname',
              })}
              inputProps={{
                'data-testid': 'last-name',
              }}
              fullWidth
              disabled
              {...registerInput(register, 'lastName')}
              {...getErrorProps(intl, errors.lastName)}
            />
          </Flex>
          <Flex mt={12}>
            <DatePicker
              label={intl.formatMessage({ id: 'signUp.form.birthDate' })}
              value={user?.basicDetails.dateOfBirth}
              onChange={noop}
              locked
            />
          </Flex>
        </Flex>
        <Input
          label={intl.formatMessage({
            id: 'profile.emailAddress',
          })}
          fullWidth
          {...registerInput(register, 'email')}
          {...getErrorProps(intl, errors.email)}
          locked
          inputProps={{
            'data-testid': 'email-address',
          }}
        />
        <StyledRowContainer>
          <Box sx={{ width: { xs: '100%', md: '35%' }, minWidth: 160 }}>
            <Controller
              control={control}
              name='phoneCountryCode'
              render={({ field }) => (
                <Dropdown
                  label={intl.formatMessage({
                    id: 'investment.contactDetails.phoneCountryCode',
                  })}
                  menuItemData={countryDialCodesWithFlag}
                  {...getErrorProps(intl, errors.phoneCountryCode)}
                  {...field}
                />
              )}
            />
          </Box>
          <Input
            label={intl.formatMessage({
              id: 'investment.contactDetails.mobileNumber',
            })}
            sx={{ width: { xs: '100%', md: '65%' } }}
            {...registerInput(register, 'phoneNumber')}
            {...getErrorProps(intl, errors.phoneNumber)}
          />
        </StyledRowContainer>
        <Flex flexDirection='column'>
          <Text
            variant='h6'
            fontWeight='fontWeightSemiBold'
            color={theme.palette.black.A100}
            mb={5}
          >
            <FormattedMessage id='profile.address' />
          </Text>
          <Input
            label={intl.formatMessage({
              id: 'investment.contactDetails.streetAddress',
            })}
            {...registerInput(register, 'address')}
            {...getErrorProps(intl, errors.address)}
          />
        </Flex>
        <StyledRowContainer>
          <Input
            label={intl.formatMessage({
              id: 'investment.contactDetails.city',
            })}
            fullWidth
            {...registerInput(register, 'city')}
            {...getErrorProps(intl, errors.city)}
          />
          <Input
            label={intl.formatMessage({
              id: 'investment.contactDetails.state',
            })}
            fullWidth
            {...registerInput(register, 'state')}
            {...getErrorProps(intl, errors.state)}
          />
        </StyledRowContainer>
        <StyledRowContainer>
          <Input
            label={intl.formatMessage({
              id: 'investment.contactDetails.zipCode',
            })}
            fullWidth
            sx={{ maxWidth: { xs: '100%', md: 'calc(50%)' } }}
            {...registerInput(register, 'zipCode')}
            {...getErrorProps(intl, errors.zipCode)}
          />
          <Box sx={{ width: { xs: '100%', md: 'calc(50%)' } }}>
            <Controller
              control={control}
              name='contactDetailsCountry'
              render={({ field }) => (
                <Dropdown
                  label={intl.formatMessage({
                    id: 'investment.contactDetails.country',
                  })}
                  fullWidth
                  menuItemData={countriesMenuItems}
                  {...field}
                />
              )}
            />
          </Box>
        </StyledRowContainer>
        <StyledButton
          disabled={!isValid || Object.keys(dirtyFields).length === 0}
          type='submit'
          data-testid='personal-save-button'
        >
          <FormattedMessage id='misc.save' />
        </StyledButton>
      </Flex>
    </form>
  );
};

export default UpdatePersonalDetails;
